import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CategoryWithMonthData } from '../types/category';
import { MoneyInput } from './MoneyInput';
import { MonthCalendarPicker } from './MonthCalendarPicker';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

interface SubCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: SubCategoryFormData & { parent_category_id?: string }) => Promise<void>;
  category?: CategoryWithMonthData | null;
  title: string;
  isGoalCreationMode?: boolean;
  parentCategories?: Array<{ id: string; name: string }>;
}

// Allocation mode: how user wants to manage this category
type AllocationMode = 'recurring' | 'manual' | 'goal';

export interface SubCategoryFormData {
  name: string;
  allocated: number;
  same_allocation: boolean;
  is_installment: boolean;
  installment_term: number | null;
  installment_progress: number | null;

  // Goal-based saving fields
  allocation_mode: AllocationMode;
  is_goal: boolean;
  goal_target: number | null;
  goal_has_target_month: boolean;
  goal_target_month: string | null;
  goal_monthly_plan: number | null; // User-specified monthly savings for non-target goals
}

export const SubCategoryModal: React.FC<SubCategoryModalProps> = ({
  isOpen,
  onClose,
  onSave,
  category,
  title,
  isGoalCreationMode = false,
  parentCategories = [],
}) => {

  const [formData, setFormData] = useState<SubCategoryFormData>({
    name: '',
    allocated: 0,
    same_allocation: true,
    is_installment: false,
    installment_term: null,
    installment_progress: null,

    // Goal-based saving defaults
    allocation_mode: isGoalCreationMode ? 'goal' : 'recurring',
    is_goal: isGoalCreationMode,
    goal_target: null,
    goal_has_target_month: false,
    goal_target_month: null,
    goal_monthly_plan: null,
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAllocationDropdown, setShowAllocationDropdown] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<string>('');
  const [goalTargetTab, setGoalTargetTab] = useState<'tanpa_target' | 'dengan_target'>('tanpa_target');
  const [isDesktop, setIsDesktop] = useState(false);
  const { t } = useTranslation();
  const { formatMoney } = useUserPreferences();

  // Desktop detection (lg: 1024px+)
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Track previous isOpen state to only reset form when modal opens (not when already open)
  const prevIsOpenRef = useRef(isOpen);

  useEffect(() => {
    // Only reset form data when modal transitions from closed to open
    const wasJustOpened = isOpen && !prevIsOpenRef.current;

    if (wasJustOpened) {
      if (category) {
        // Determine allocation mode from existing category
        let mode: AllocationMode = 'recurring';
        if (category.is_goal) {
          mode = 'goal';
        } else if (!category.same_allocation) {
          mode = 'manual';
        }

        setFormData({
          name: category.name,
          allocated: category.allocated || 0,
          same_allocation: category.same_allocation,
          is_installment: category.is_installment,
          installment_term: category.installment_term,
          installment_progress: category.installment_progress,

          // Goal-based saving
          allocation_mode: mode,
          is_goal: category.is_goal || false,
          goal_target: category.goal_target || null,
          goal_has_target_month: !!category.goal_target_month,
          goal_target_month: category.goal_target_month || null,
          goal_monthly_plan: category.goal_monthly_target || null, // Load from goal_monthly_target
        });

        // Set the correct tab based on whether goal has target month
        if (category.is_goal) {
          setGoalTargetTab(category.goal_target_month ? 'dengan_target' : 'tanpa_target');
        }
      } else {
        setFormData({
          name: '',
          allocated: 0,
          same_allocation: true,
          is_installment: false,
          installment_term: null,
          installment_progress: null,

          // Goal-based saving defaults (force goal mode if isGoalCreationMode)
          allocation_mode: isGoalCreationMode ? 'goal' : 'recurring',
          is_goal: isGoalCreationMode,
          goal_target: null,
          goal_has_target_month: false,
          goal_target_month: null,
          goal_monthly_plan: null,
        });
        // Reset to default tab for new goals
        setGoalTargetTab('tanpa_target');
      }
      setError('');
      setShowAllocationDropdown(false);
      setSelectedParentCategoryId(parentCategories.length > 0 ? parentCategories[0].id : '');
    }

    // Update the ref for next render
    prevIsOpenRef.current = isOpen;
  }, [isOpen, category, isGoalCreationMode, parentCategories]);

  // Handle allocation mode change
  const handleAllocationModeChange = (mode: AllocationMode) => {
    setFormData({
      ...formData,
      allocation_mode: mode,
      same_allocation: mode === 'recurring',
      is_goal: mode === 'goal',
      goal_target: mode === 'goal' ? formData.allocated : null,
    });
    setShowAllocationDropdown(false);
  };

  // Get label for the current allocation mode
  const getAllocationModeLabel = () => {
    switch (formData.allocation_mode) {
      case 'recurring':
        return t('subcategory_modal.allocate_recurring', { amount: formatMoney(formData.allocated) });
      case 'manual':
        return t('subcategory_modal.allocate_manual');
      case 'goal':
        return t('subcategory_modal.save_until', { amount: formatMoney(formData.allocated) });
      default:
        return t('subcategory_modal.select_mode');
    }
  };

  // Generate available months for goal target (next 5 years)
  const generateAvailableMonths = () => {
    const months: string[] = [];
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() + 1); // Start from next month
    startDate.setDate(1);

    for (let i = 0; i < 60; i++) { // 60 months = 5 years
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, '0');
      months.push(`${year}-${month}-01`);
      startDate.setMonth(startDate.getMonth() + 1);
    }
    return months;
  };

  // Format month for display (e.g., "Jan 2025")
  const formatMonthDisplay = (dateString: string | null) => {
    if (!dateString) return t('subcategory_modal.select_target_month');

    // If dateString is YYYY-MM format, append -01 for proper Date parsing
    const fullDateString = dateString.length === 7 ? `${dateString}-01` : dateString;
    const date = new Date(fullDateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return t('subcategory_modal.select_target_month');

    const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const monthName = t(`budget.months_short.${monthKeys[date.getMonth()]}`);
    return `${monthName} ${date.getFullYear()}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Goal creation mode validation
    if (isGoalCreationMode && !selectedParentCategoryId) {
      setError(t('subcategory_modal.select_group'));
      return;
    }

    // Validation
    if (formData.name.trim().length < 2) {
      setError(t('subcategory_modal.category_name_error'));
      return;
    }

    if (formData.allocated <= 0) {
      setError(t('subcategory_modal.allocation_error'));
      return;
    }

    // Goal-specific validation
    if (formData.allocation_mode === 'goal') {
      if (goalTargetTab === 'tanpa_target') {
        // Validate monthly savings plan for goals without target month
        if (!formData.goal_monthly_plan || formData.goal_monthly_plan <= 0) {
          setError(t('subcategory_modal.monthly_savings_error'));
          return;
        }
      } else if (goalTargetTab === 'dengan_target') {
        // Validate target month for goals with target month
        if (formData.goal_has_target_month && formData.goal_target_month) {
          // Append -01 to YYYY-MM format for proper date comparison
          const targetDateString = formData.goal_target_month.length === 7 ? `${formData.goal_target_month}-01` : formData.goal_target_month;
          const targetDate = new Date(targetDateString);
          const currentDate = new Date();
          currentDate.setDate(1); // Reset to first of month for fair comparison

          if (targetDate <= currentDate) {
            setError(t('subcategory_modal.target_month_error'));
            return;
          }
        }
      }
    }

    if (formData.is_installment) {
      if (!formData.installment_term || formData.installment_term <= 0) {
        setError(t('subcategory_modal.installment_term_error'));
        return;
      }
      if (
        formData.installment_progress !== null &&
        formData.installment_term &&
        formData.installment_progress > formData.installment_term
      ) {
        setError(t('subcategory_modal.installment_progress_error'));
        return;
      }
    }

    setLoading(true);
    try {
      // Include parent_category_id if in goal creation mode
      const dataToSave = isGoalCreationMode
        ? { ...formData, parent_category_id: selectedParentCategoryId }
        : formData;
      await onSave(dataToSave);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('subcategory_modal.save_error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-lg w-full max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: '4px 4px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4 flex items-center justify-between sticky top-0 z-10">
          <h2
            className="text-white uppercase"
            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-[#077f5d]"
            style={{ border: '2px solid #077f5d' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Grup dropdown - Only shown in goal creation mode */}
          {isGoalCreationMode && (
            <div>
              <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
                {t('subcategory_modal.group')} <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedParentCategoryId}
                onChange={(e) => setSelectedParentCategoryId(e.target.value)}
                className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                style={{ boxShadow: '4px 4px 0 #077f5d' }}
              >
                {parentCategories.map((parent) => (
                  <option key={parent.id} value={parent.id}>
                    {parent.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              {t('subcategory_modal.category_name')}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
              style={{ boxShadow: '4px 4px 0 #077f5d' }}
              placeholder={t('subcategory_modal.category_name_placeholder')}
              autoFocus
            />
          </div>

          {/* Allocated Amount / Target Amount */}
          <div>
            <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              {formData.allocation_mode === 'goal' ? t('subcategory_modal.goal_target') : t('subcategory_modal.allocation')}
            </label>
            <MoneyInput
              value={formData.allocated}
              onValueChange={(value) => {
                setFormData({
                  ...formData,
                  allocated: value,
                  goal_target: formData.allocation_mode === 'goal' ? value : formData.goal_target,
                });
              }}
              className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
              style={{ boxShadow: '4px 4px 0 #077f5d' }}
              placeholder={formatMoney(0)}
              allowNegative={false}
              useCustomKeyboard={!isDesktop}
              keyboardTitle={formData.allocation_mode === 'goal' ? t('subcategory_modal.goal_target') : t('subcategory_modal.allocation')}
            />
          </div>

          {/* Aku Mau Dropdown - Replaces "Alokasi sama tiap bulan" checkbox */}
          <div className="relative">
            <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
              {t('subcategory_modal.i_want')}
            </label>
            <button
              type="button"
              onClick={() => !isGoalCreationMode && setShowAllocationDropdown(!showAllocationDropdown)}
              disabled={isGoalCreationMode}
              className={`w-full px-4 py-3 border-2 border-[#077f5d] font-mono text-sm focus:outline-none flex items-center justify-between ${isGoalCreationMode
                  ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  : 'bg-white text-[#1a1a1a] focus:border-[#3cbe8c]'
                }`}
              style={{ boxShadow: '4px 4px 0 #077f5d', transition: 'none' }}
            >
              <span>{getAllocationModeLabel()}</span>
              {!isGoalCreationMode && (
                <ChevronDown size={16} className={`text-[#077f5d] ${showAllocationDropdown ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.1s' }} />
              )}
            </button>

            {/* Dropdown Menu - Only shown when NOT in goal creation mode */}
            {!isGoalCreationMode && showAllocationDropdown && (
              <div
                className="absolute z-10 w-full bg-white border-2 border-[#077f5d] overflow-hidden"
                style={{ boxShadow: '4px 4px 0 #077f5d', top: '50%' }}
              >
                <button
                  type="button"
                  onClick={() => handleAllocationModeChange('recurring')}
                  className={`w-full px-4 py-3 text-left text-sm font-mono hover:bg-[#dffbd0] ${formData.allocation_mode === 'recurring' ? 'bg-[#3cbe8c] text-white font-bold' : 'text-[#1a1a1a]'
                    }`}
                  style={{ transition: 'none' }}
                >
                  {t('subcategory_modal.allocate_recurring', { amount: formatMoney(formData.allocated) })}
                </button>
                <button
                  type="button"
                  onClick={() => handleAllocationModeChange('manual')}
                  className={`w-full px-4 py-3 text-left text-sm font-mono hover:bg-[#dffbd0] border-t-2 border-[#077f5d] ${formData.allocation_mode === 'manual' ? 'bg-[#3cbe8c] text-white font-bold' : 'text-[#1a1a1a]'
                    }`}
                  style={{ transition: 'none' }}
                >
                  {t('subcategory_modal.allocate_manual')}
                </button>
                <button
                  type="button"
                  onClick={() => handleAllocationModeChange('goal')}
                  className={`w-full px-4 py-3 text-left text-sm font-mono hover:bg-[#dffbd0] border-t-2 border-[#077f5d] ${formData.allocation_mode === 'goal' ? 'bg-[#3cbe8c] text-white font-bold' : 'text-[#1a1a1a]'
                    }`}
                  style={{ transition: 'none' }}
                >
                  {t('subcategory_modal.save_until', { amount: formatMoney(formData.allocated) })}
                </button>
              </div>
            )}
          </div>

          {/* Conditional UI for Goal Mode */}
          {formData.allocation_mode === 'goal' ? (
            <div>
              {/* Tabs for goal target month */}
              <div className="flex border-2 border-[#077f5d]" style={{ boxShadow: '3px 3px 0 #077f5d' }}>
                <button
                  type="button"
                  onClick={() => {
                    setGoalTargetTab('tanpa_target');
                    setFormData({
                      ...formData,
                      goal_has_target_month: false,
                      goal_target_month: null,
                    });
                  }}
                  className={`flex-1 px-4 py-2 font-mono font-bold text-sm ${goalTargetTab === 'tanpa_target'
                      ? 'bg-[#3cbe8c] text-white'
                      : 'bg-white text-[#077f5d] hover:bg-[#dffbd0]'
                    }`}
                  style={{ transition: 'none' }}
                >
                  {t('subcategory_modal.without_target')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setGoalTargetTab('dengan_target');
                  }}
                  className={`flex-1 px-4 py-2 font-mono font-bold text-sm border-l-2 border-[#077f5d] ${goalTargetTab === 'dengan_target'
                      ? 'bg-[#3cbe8c] text-white'
                      : 'bg-white text-[#077f5d] hover:bg-[#dffbd0]'
                    }`}
                  style={{ transition: 'none' }}
                >
                  {t('subcategory_modal.with_target')}
                </button>
              </div>

              {/* Tab content */}
              <div
                className="bg-[#dffbd0] border-2 border-t-0 border-[#077f5d] p-3"
                style={{ boxShadow: '3px 3px 0 #077f5d' }}
              >
                {goalTargetTab === 'tanpa_target' ? (
                  // Tab 1: Tanpa target bulan - show monthly savings field
                  <div>
                    <label className="block font-mono font-bold text-xs text-[#077f5d] mb-2 uppercase">
                      {t('subcategory_modal.monthly_savings')}
                    </label>
                    <MoneyInput
                      value={formData.goal_monthly_plan || 0}
                      onValueChange={(value) => {
                        setFormData({
                          ...formData,
                          goal_monthly_plan: value,
                        });
                      }}
                      className="w-full px-4 py-3 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                      style={{ boxShadow: '4px 4px 0 #077f5d' }}
                      placeholder={formatMoney(0)}
                      allowNegative={false}
                      useCustomKeyboard={!isDesktop}
                      keyboardTitle={t('subcategory_modal.monthly_savings')}
                    />
                  </div>
                ) : (
                  // Tab 2: Dengan target bulan - show checkbox and month picker
                  <div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMonthPicker(true);
                      }}
                    >
                      <button
                        type="button"
                        className="w-full px-4 py-3 mt-2 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c] text-left flex items-center gap-3"
                        style={{ boxShadow: '4px 4px 0 #077f5d' }}
                      >
                        <Calendar size={20} className="text-[#077f5d] flex-shrink-0" />
                        <span>{formatMonthDisplay(formData.goal_target_month)}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Installment Toggle */}
              <div
                className="bg-[#dffbd0] border-2 border-[#077f5d] p-2"
                style={{ boxShadow: '3px 3px 0 #077f5d' }}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="is_installment"
                    checked={formData.is_installment}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_installment: e.target.checked,
                        installment_term: null,
                        installment_progress: null,
                      })
                    }
                    className="w-5 h-5 border-2 border-[#077f5d] mt-0.5"
                    style={{ accentColor: '#3cbe8c' }}
                  />
                  <label htmlFor="is_installment" className="font-mono font-bold text-sm text-[#077f5d] cursor-pointer">
                    {t('subcategory_modal.installment')}
                  </label>
                </div>

                {/* Installment Fields */}
                {formData.is_installment && (
                  <div className="mt-2 mb-1 flex items-center gap-3">
                    <input
                      type="number"
                      value={formData.installment_progress ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          installment_progress: e.target.value === '' ? null : parseInt(e.target.value),
                        })
                      }
                      className="w-full px-2 py-2 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                      style={{ boxShadow: '3px 3px 0 #077f5d' }}
                      placeholder="3"
                      min="0"
                    />

                    <span className="font-mono font-bold text-xl text-[#077f5d] flex-shrink-0">/</span>

                    <input
                      type="number"
                      value={formData.installment_term ?? ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          installment_term: e.target.value === '' ? null : parseInt(e.target.value),
                        })
                      }
                      className="w-full px-2 py-2 border-2 border-[#077f5d] bg-white font-mono text-[#1a1a1a] focus:outline-none focus:border-[#3cbe8c]"
                      style={{ boxShadow: '3px 3px 0 #077f5d' }}
                      placeholder="12"
                      min="1"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {error && (
            <div
              className="bg-red-500 border-2 border-red-700 px-3 py-2"
              style={{ boxShadow: '3px 3px 0 #c0392b' }}
            >
              <p className="font-mono text-xs text-white">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '4px 4px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(-2px, -2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              {t('subcategory_modal.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold uppercase disabled:opacity-50"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '6px 6px 0 #077f5d'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
            >
              {loading ? t('subcategory_modal.saving') : t('subcategory_modal.save')}
            </button>
          </div>
        </form>
      </div>

      {/* Month Calendar Picker Modal */}
      {showMonthPicker && (
        <MonthCalendarPicker
          availableMonths={generateAvailableMonths()}
          currentMonth={
            formData.goal_target_month
              ? `${formData.goal_target_month}-01`
              : generateAvailableMonths()[0] // Default to first available month (next month)
          }
          onSelectMonth={(month) => {
            // Convert YYYY-MM-DD to YYYY-MM format for backend
            const formattedMonth = month.substring(0, 7); // Extract first 7 characters (YYYY-MM)
            setFormData({
              ...formData,
              goal_target_month: formattedMonth,
            });
            setShowMonthPicker(false);
          }}
          onClose={() => setShowMonthPicker(false)}
        />
      )}
    </div>
  );
};
